import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Key, Save, Loader2, User } from 'lucide-react';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileForm, setProfileForm] = useState({
    full_name: ''
  });
  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setProfileForm({ full_name: data.user.full_name || '' });
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }
    setLoading(false);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setMessage(null);
    setSaving(true);

    try {
      const res = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profileForm)
      });

      const data = await res.json();

      if (res.ok) {
        setMessage({ type: 'success', text: 'Profil úspěšně aktualizován' });
        fetchUserInfo();
      } else {
        setMessage({ type: 'error', text: data.error || 'Chyba při aktualizaci profilu' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Chyba spojení' });
    }
    setSaving(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: 'Nová hesla se neshodují' });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Heslo musí mít alespoň 6 znaků' });
      return;
    }

    setSaving(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      const data = await res.json();

      if (res.ok) {
        setMessage({ type: 'success', text: 'Heslo úspěšně změněno' });
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Chyba při změně hesla' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Chyba spojení' });
    }
    setSaving(false);
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #E0E0E0',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: '#333',
    marginBottom: '6px'
  };

  if (loading) {
    return (
      <div style={{ padding: '32px', color: '#666', textAlign: 'center' }}>
        <Loader2 size={24} className="spin" />
        <p>Načítám...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#333', margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <SettingsIcon color="#E53935" /> Nastavení
      </h1>

      <div style={{ backgroundColor: '#FFF', borderRadius: '12px', border: '1px solid #E0E0E0', padding: '24px' }}>
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #E0E0E0', marginBottom: '24px', paddingBottom: '12px' }}>
          <button
            onClick={() => { setActiveTab('profile'); setMessage(null); }}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: activeTab === 'profile' ? '#E53935' : '#F5F5F5',
              color: activeTab === 'profile' ? '#FFF' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <User size={16} /> Profil
          </button>
          <button
            onClick={() => { setActiveTab('password'); setMessage(null); }}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: activeTab === 'password' ? '#E53935' : '#F5F5F5',
              color: activeTab === 'password' ? '#FFF' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Key size={16} /> Změna hesla
          </button>
        </div>

        {message && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              backgroundColor: message.type === 'success' ? '#E8F5E9' : '#FFEBEE',
              color: message.type === 'success' ? '#388E3C' : '#E53935',
              fontSize: '14px'
            }}
          >
            {message.text}
          </div>
        )}

        {activeTab === 'profile' && (
          <div style={{ maxWidth: '500px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#333', margin: '0 0 16px' }}>
              Uživatelský profil
            </h3>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#F5F5F5', borderRadius: '8px' }}>
              <p style={{ fontSize: '14px', color: '#666', margin: '0 0 4px' }}>Uživatelské jméno:</p>
              <p style={{ fontSize: '16px', fontWeight: 600, color: '#333', margin: 0 }}>
                {user?.username}
              </p>
            </div>

            <form onSubmit={handleProfileUpdate}>
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Celé jméno</label>
                <input
                  type="text"
                  value={profileForm.full_name}
                  onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                  style={inputStyle}
                  placeholder="Zadejte vaše celé jméno"
                />
                <p style={{ fontSize: '12px', color: '#999', margin: '4px 0 0' }}>
                  Toto jméno se zobrazí v aplikaci místo uživatelského jména
                </p>
              </div>

              <button
                type="submit"
                disabled={saving}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: '#E53935',
                  color: '#FFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  opacity: saving ? 0.6 : 1
                }}
              >
                {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
                {saving ? 'Ukládám...' : 'Uložit profil'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'password' && (
          <div style={{ maxWidth: '500px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#333', margin: '0 0 16px' }}>
              Změnit heslo
            </h3>

            <form onSubmit={handlePasswordChange}>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Současné heslo</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  style={inputStyle}
                  required
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Nové heslo</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  style={inputStyle}
                  required
                  minLength={6}
                />
                <p style={{ fontSize: '12px', color: '#999', margin: '4px 0 0' }}>
                  Heslo musí mít alespoň 6 znaků
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Potvrdit nové heslo</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  style={inputStyle}
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: '#E53935',
                  color: '#FFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  opacity: saving ? 0.6 : 1
                }}
              >
                {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
                {saving ? 'Ukládám...' : 'Uložit nové heslo'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
